import { itemRouteSegment } from "@p0security/shared/install/routes";
import { AppPaths } from "@p0security/shared/routes/constants";
import type { AgenticContext } from "@p0security/shared/types/permission";
import { Card, Descriptions, Flex, Tooltip } from "@p0security/ui";
import {
  DelegationChain,
  delegatesOf,
} from "components/Agentic/DelegationChain";
import { McpClientIdIcon } from "components/Agentic/clientPlatforms";
import { AgenticPaths } from "components/Agentic/paths";
import { DetailsDescriptions } from "components/Jit/DetailsDescriptions";
import { TenantAwareLink } from "components/common/TenantAwareLink";
import { useMemo } from "react";
import { Link } from "react-router-dom";

export const AgenticDetails: React.FC<{
  agentic: AgenticContext;
  subject: string;
}> = ({ agentic, subject }) => {
  const delegates = useMemo(
    () => delegatesOf(agentic.agents, subject),
    [agentic.agents, subject]
  );
  return (
    <Card title="Agentic Details">
      <DetailsDescriptions>
        {agentic.gatewayId && (
          <Descriptions.Item label="Gateway">
            <Link
              to={{
                pathname: `../../integrations/agentic/gateway/${itemRouteSegment(agentic.gatewayId)}`,
              }}
            >
              {agentic.gatewayId}
            </Link>
          </Descriptions.Item>
        )}
        {agentic.identityProviderId && (
          <Descriptions.Item label="Identity Provider">
            {agentic.identityProviderId}
          </Descriptions.Item>
        )}
        {!agentic.headless && agentic.agents?.[0]?.sub && (
          <Descriptions.Item
            label={
              <Tooltip title="The MCP client that initiated this request on behalf of the user">
                Client ID
              </Tooltip>
            }
          >
            <Flex align="center" gap={6}>
              <McpClientIdIcon clientId={agentic.agents[0].sub} />
              {agentic.agents[0].sub}
            </Flex>
          </Descriptions.Item>
        )}
        {agentic.sessionId && (
          <Descriptions.Item label="Agent Session">
            <TenantAwareLink
              to={`${AppPaths.AgenticAccess}/${AgenticPaths.EventLog}?sessionId=${encodeURIComponent(agentic.sessionId)}`}
            >
              {agentic.sessionId}
            </TenantAwareLink>
          </Descriptions.Item>
        )}
        {agentic.session?.sub && (
          // span={2} ensures that the next item has room to span
          <Descriptions.Item label="Session Identity" span={2}>
            {agentic.session.sub}
          </Descriptions.Item>
        )}
        <Descriptions.Item
          label={
            <Tooltip
              title="
                The acting agent / client appears first, the originating agent /
                client appears last"
            >
              Delegation Chain
            </Tooltip>
          }
          span={2}
        >
          <DelegationChain delegates={delegates} />
        </Descriptions.Item>
      </DetailsDescriptions>
    </Card>
  );
};
